import {getTestProps} from '@benchling/js-test-utils';
import {
  Avatar,
  createStyles,
  Group,
  type MantineTransition,
  Text,
  Transition,
  UnstyledButton,
} from '@mantine/core';
import React, {useCallback, useEffect, useState} from 'react';

import navigateToURL from 'lib/navigateToURL';
import {
  AGENT_AVATAR_COLORS,
  REDESIGN_DRAWER_BACKGROUND,
  REDESIGN_DRAWER_TRANSITION_MS,
} from 'llm/chat-library/ui/AIStyleConstants';
import {agentHomepageUrl} from 'llm/routes/UrlUtils';
import type {Routine} from 'llm/routines/RoutineTypes';
import {getAvatarInitials} from 'ui-library/avatar/Avatar';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {getAgentDisplayName} from './SidebarAgentsList';
import SidebarSectionHeader from './SidebarSectionHeader';

const AGENTS_PAGE_SIZE = 15;

// Slide the surface in from / out to the right edge. Driving both directions off one transition means
// the Back button (and drawer close) animate the panel away, mirroring how it slid in.
const slideFromRight: MantineTransition = {
  in: {transform: 'translateX(0)'},
  out: {transform: 'translateX(100%)'},
  transitionProperty: 'transform',
};

const useStyles = createStyles((theme) => ({
  surface: {
    display: 'flex',
    flex: 1,
    width: '100%',
    minHeight: 0,
    boxSizing: 'border-box',
    flexDirection: 'column',
    // Opaque, matching the sidebar, so the panel fully covers the nav/history it slides over — edge
    // to edge, including any padding, so no sliver of the covered list shows through.
    backgroundColor: REDESIGN_DRAWER_BACKGROUND,
    // The wrapper disables pointer events so the empty (closed) overlay never blocks the sidebar;
    // re-enable them on the visible surface.
    pointerEvents: 'auto',
  },
  back: {
    alignSelf: 'flex-start',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    color: theme.colors.dark[7],
  },
  list: {
    display: 'flex',
    // Fill the remaining panel height and scroll internally, so the list never spills past the
    // opaque panel and reveals the covered content beneath.
    flex: 1,
    minHeight: 0,
    flexDirection: 'column',
    gap: theme.spacing.xs,
    overflowY: 'auto',
  },
  agent: {
    width: '100%',
    borderRadius: theme.radius.sm,
    padding: `${theme.spacing.sm}px ${theme.spacing.sm}px`,
    '&:hover': {
      backgroundColor: theme.colors.gray[0],
    },
  },
  avatar: {
    flex: 'none',
  },
  avatarPlaceholder: AGENT_AVATAR_COLORS,
  name: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  viewMore: {
    alignSelf: 'flex-start',
    // Match the "Back" affordance and the strip's chevron rather than a standalone blue.
    color: theme.colors.dark[7],
    padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
  },
}));

function AgentRow({routine, index}: {routine: Routine; index: number}): JSX.Element {
  const {classes} = useStyles();
  const displayName = getAgentDisplayName(routine.name);
  const handleClick = useCallback(() => {
    navigateToURL(agentHomepageUrl(routine.id));
  }, [routine.id]);

  return (
    <UnstyledButton
      {...getTestProps({component: 'SidebarAgentsPanel', element: 'agentRow', index: String(index)})}
      className={classes.agent}
      aria-label={displayName}
      onClick={handleClick}
    >
      <Group spacing="sm" noWrap={true}>
        <Avatar
          radius="xl"
          size="md"
          className={classes.avatar}
          classNames={{placeholder: classes.avatarPlaceholder}}
        >
          {getAvatarInitials({name: displayName})}
        </Avatar>
        <Text size="md" className={classes.name}>
          {displayName}
        </Text>
      </Group>
    </UnstyledButton>
  );
}

export interface SidebarAgentsPanelProps {
  /** The agents to list, or null when the panel is closed (drives the slide in/out). */
  routines: Array<Routine> | null;
  onBack: () => void;
  /** Extra class applied to the panel surface, e.g. per-view padding. */
  className?: string;
}

/**
 * Full-sidebar overlay listing every agent, slid in over the chat history. Always mounted; the
 * `routines` prop toggles it (null = closed) and a slide transition animates both open and close.
 * Callers position/clip it via an absolute wrapper and supply any per-view padding through `className`.
 */
export default function SidebarAgentsPanel({
  routines,
  onBack,
  className,
}: SidebarAgentsPanelProps): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes, cx} = useStyles();
  const [visibleCount, setVisibleCount] = useState(AGENTS_PAGE_SIZE);
  // Retain the last opened list so the slide-out still has content to render while closing.
  const [displayedRoutines, setDisplayedRoutines] = useState<Array<Routine>>([]);
  useEffect(() => {
    if (routines != null) {
      setDisplayedRoutines(routines);
      setVisibleCount(AGENTS_PAGE_SIZE);
    }
  }, [routines]);

  const visibleRoutines = displayedRoutines.slice(0, visibleCount);

  return (
    <Transition
      mounted={routines != null}
      transition={slideFromRight}
      duration={REDESIGN_DRAWER_TRANSITION_MS}
      timingFunction="ease"
    >
      {(transitionStyle) => (
        <div
          className={cx(classes.surface, className)}
          style={transitionStyle}
          {...getTestProps({component: 'SidebarAgentsPanel', element: 'root'})}
        >
          <UnstyledButton
            {...getTestProps({component: 'SidebarAgentsPanel', element: 'backButton'})}
            className={classes.back}
            aria-label="Back to chat history"
            onClick={onBack}
          >
            <BenchlingIcon icon="arrow-left" />
            <Text size="sm">Back</Text>
          </UnstyledButton>
          <SidebarSectionHeader>Agents</SidebarSectionHeader>
          <div className={classes.list}>
            {visibleRoutines.map((routine, index) => (
              <AgentRow key={routine.id} routine={routine} index={index} />
            ))}
            {visibleCount < displayedRoutines.length && (
              <UnstyledButton
                {...getTestProps({component: 'SidebarAgentsPanel', element: 'viewMore'})}
                className={classes.viewMore}
                onClick={() => {
                  setVisibleCount((count) => count + AGENTS_PAGE_SIZE);
                }}
              >
                View more
              </UnstyledButton>
            )}
          </div>
        </div>
      )}
    </Transition>
  );
}
